import { AppRoutes } from "@/shared/lib/routes"
import { createRoute, useRouterState } from "@tanstack/react-router"
import { useEffect, useMemo } from "react"
import { rootRoute } from "./__root"
import { usePlans, productsService } from "@/features/products"
import { useUpsell } from "@/features/payments"
import { ANALYTICS_EVENTS, trackEvent } from "@/features/analytics"
import { useNavigateWithParams } from "@/shared/hooks/use-navigate-with-params"
import { MobileLayout } from "@/shared/components/mobile-layout"
import { LoadingSpinner } from "@/shared/components/loading-spinner"
import { formatMoney } from "@/shared/lib/money"

export const upsellRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: AppRoutes.UPSELL,
  component: UpsellPage,
})

function UpsellPage() {
  const navigate = useNavigateWithParams()
  const search = useRouterState({ select: (s) => s.location.search }) as Record<string, unknown>
  const upsellId = typeof search.upsell === "string" ? search.upsell : ""

  const ids = useMemo(() => (upsellId ? [upsellId] : []), [upsellId])
  const { plans, status } = usePlans(ids)
  const plan = plans[0]

  const { purchase, status: upsellStatus, error, canPurchase } = useUpsell()

  // No upsell configured (or no backend) → skip straight to sign-in.
  const offerAvailable = Boolean(upsellId) && productsService.isConfigured && canPurchase

  useEffect(() => {
    if (!offerAvailable) {
      navigate({ to: AppRoutes.CREATE_ACCOUNT })
    }
  }, [offerAvailable, navigate])

  useEffect(() => {
    if (plan) {
      trackEvent(ANALYTICS_EVENTS.PURCHASE_PAGE_VIEWED, {
        productId: plan.id,
        price: plan.price,
        currency: plan.currency,
        metadata: { kind: "upsell" },
      })
    }
  }, [plan])

  const goNext = () => navigate({ to: AppRoutes.CREATE_ACCOUNT })

  const handleAdd = async () => {
    if (!plan) return
    await purchase(plan)
    goNext()
  }

  if (!offerAvailable || status === "loading" || !plan) {
    return (
      <div className="flex min-h-[100dvh] items-center justify-center bg-background">
        <LoadingSpinner />
      </div>
    )
  }

  const processing = upsellStatus === "processing"

  return (
    <div className="flex min-h-[100dvh] flex-col bg-background">
      <MobileLayout className="flex flex-1 flex-col justify-center py-10">
        <div className="text-center">
          <span className="inline-flex items-center rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-700">
            One-time offer
          </span>
          <h1 className="mt-4 text-2xl font-bold leading-tight">{plan.fullName || plan.name}</h1>
          {plan.description && (
            <p className="mt-2 text-sm text-muted-foreground">{plan.description}</p>
          )}
        </div>

        <div className="mt-6 rounded-2xl border-2 border-primary bg-card p-5 text-center">
          <div className="flex items-baseline justify-center gap-2">
            {plan.previousPrice && (
              <span className="text-base text-muted-foreground line-through">
                {formatMoney(plan.previousPrice, plan.currency)}
              </span>
            )}
            <span className="text-3xl font-bold">{formatMoney(plan.price, plan.currency)}</span>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">Charged to your card on file</p>
        </div>

        {error && <p className="mt-3 text-center text-sm text-red-500">{error}</p>}

        <div className="mt-6 space-y-3">
          <button
            type="button"
            onClick={handleAdd}
            disabled={processing}
            className="flex h-14 w-full items-center justify-center rounded-2xl bg-primary text-base font-semibold text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
          >
            {processing ? "Adding…" : `Add to my order — ${formatMoney(plan.price, plan.currency)}`}
          </button>
          <button
            type="button"
            onClick={goNext}
            disabled={processing}
            className="flex h-12 w-full items-center justify-center text-sm font-medium text-muted-foreground underline disabled:opacity-50"
          >
            No thanks
          </button>
        </div>
      </MobileLayout>
    </div>
  )
}
